import {
  Eye,
  Star,
  Power,
  PowerOff,
  Trash2,
  Edit3,
  MoreVertical,
  Loader,
} from "lucide-react";
import { getStockBadge, formatPrice } from "../../utils/productUtils";
import { useState, useEffect, useRef } from "react";

function ProductRowActions({
  product,
  onTogglePopular,
  onToggleStatus,
  loadingPopular,
  loadingStatus
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="p-2 rounded-lg text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-all active:scale-90"
      >
        <MoreVertical size={16} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl shadow-xl py-2 z-30 animate-in fade-in slide-in-from-top-2 duration-200">
          <button
            onClick={() => {
              onTogglePopular(product.id, product.popular);
              setOpen(false);
            }}
            disabled={loadingPopular}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingPopular ? (
              <Loader size={14} className="animate-spin" />
            ) : (
              <Star
                size={14}
                fill={product.popular ? "currentColor" : "none"}
                className={product.popular ? "text-orange-400" : ""}
              />
            )}
            {product.popular ? "Remove Popular" : "Mark Popular"}
          </button>
          <button
            onClick={() => {
              onToggleStatus(product.id, product.status);
              setOpen(false);
            }}
            disabled={loadingStatus}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingStatus ? (
              <Loader size={14} className="animate-spin" />
            ) : product.status === "active" ? (
              <PowerOff size={14} />
            ) : (
              <Power size={14} />
            )}
            {product.status === "active" ? "Disable Product" : "Enable Product"}
          </button>
        </div>
      )}
    </div>
  );
}

export function ProductsTable({
  products,
  selectedProducts,
  allSelected,
  onSelectAll,
  onSelectProduct,
  onViewProduct,
  onEditProduct,
  onTogglePopular,
  onToggleStatus,
  loadingStatusMap = {},
  loadingPopularMap = {},
}) {
  return (
    <div className="bg-white dark:bg-[#1E1E1E] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-[#F9FAFB] dark:bg-[#1A1A1A] border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  className="w-4 h-4 rounded border-[#E5E7EB] dark:border-[#2A2A2A] text-[#62397f] focus:ring-[#62397f] cursor-pointer"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider font-inter">
                Image
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider font-inter">
                Product Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider font-inter">
                Category
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider font-inter">
                Price
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider font-inter">
                Stock
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider font-inter">
                Status
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider font-inter">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB] dark:divide-[#2A2A2A]">
            {products.map((product) => {
              const isTogglingStatus = loadingStatusMap[product.id];
              const isTogglingPopular = loadingPopularMap[product.id];

              return (
                <tr
                  key={product.id}
                  className="hover:bg-[#F9FAFB] dark:hover:bg-[#1A1A1A] transition-colors group"
                >
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.id)}
                      onChange={(e) =>
                        onSelectProduct(product.id, e.target.checked)
                      }
                      className="w-4 h-4 rounded border-[#E5E7EB] dark:border-[#2A2A2A] text-[#62397f] focus:ring-[#62397f] cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <div className="relative w-12 h-12">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-12 h-12 rounded-lg object-cover border border-[#E5E7EB] dark:border-[#2A2A2A]"
                      />
                      {product.popular && (
                        <div className="absolute -top-1.5 -right-1.5 p-0.5 bg-orange-100 text-orange-600 rounded shadow-sm">
                          <Star size={8} fill="currentColor" />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="font-semibold text-sm text-[#111111] dark:text-white font-inter truncate max-w-[200px] block">
                      {product.name}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="px-2 py-1 bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] text-xs font-medium rounded-lg">
                      {product.category}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm font-semibold text-[#111111] dark:text-white font-inter">
                      ₦{formatPrice(product.price)}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-[#666666] dark:text-[#A0A0A0] font-inter">
                      {product.quantity}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    {product.status === "active" ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 text-xs font-medium rounded-full">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 text-xs font-medium rounded-full">
                        <div className="w-1.5 h-1.5 bg-gray-500 rounded-full"></div>
                        Disabled
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => onViewProduct(product)}
                        className="p-2 rounded-lg text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] hover:text-[#62397f] transition-all"
                        title="Quick View"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => onEditProduct(product)}
                        className="p-2 rounded-lg text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] hover:text-[#62397f] transition-all"
                        title="Edit"
                      >
                        <Edit3 size={16} />
                      </button>
                      <ProductRowActions
                        product={product}
                        onTogglePopular={onTogglePopular}
                        onToggleStatus={onToggleStatus}
                        loadingPopular={isTogglingPopular}
                        loadingStatus={isTogglingStatus}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
